import { cartRoutes, writeCart } from '@theme/eq-cart-write';

/**
 * Add to cart from a promo tile in the collection grid.
 *
 * The old theme bound `.special-atc-btn` to a bare `fetch('/cart/add.js')` and then dispatched its
 * own `minicart-custom` event to refresh the drawer. The write goes through `eq-cart-write` here
 * instead, so the drawer, the cart icon and analytics hear about it the same way they do for the
 * product cards and the offer builder.
 *
 * Delegated from `document` rather than hung off the tile's `ref`: the grid re-renders in place on
 * every filter and sort, which would drop listeners bound to the old nodes.
 */

const ACTION = '.eq-collection-promo-tile__action[data-value]';

/** How long the spinner stays up after the line is in the cart, before the drawer slides in. */
const DRAWER_OPEN_DELAY = 400;

const openCartDrawer = () => {
  const drawer = document.querySelector('cart-drawer-component');
  if (!(drawer instanceof HTMLElement) || !drawer.hasAttribute('auto-open')) return;

  drawer.closest('theme-drawer')?.open();
};

/** @param {HTMLElement} button */
const addVariant = async (button) => {
  const variantId = button.dataset.value;
  if (!variantId || button.dataset.busy === 'true') return;

  button.dataset.busy = 'true';
  button.setAttribute('aria-busy', 'true');

  try {
    // Horizon's auto-open is suppressed (`openDrawer: false`) because `writeCart` dispatches its
    // event before the request leaves, which would slide the drawer in over an empty cart. The
    // spinner covers the write and the pause after it, and the drawer is opened by hand at the end.
    const cart = await writeCart({
      url: cartRoutes().add,
      body: { items: [{ id: variantId, quantity: 1 }] },
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      target: button,
      source: 'eq-collection-promo-tile',
      openDrawer: false,
    });

    if (cart) {
      await new Promise((resolve) => setTimeout(resolve, DRAWER_OPEN_DELAY));
      openCartDrawer();
    } else {
      // `writeCart` answers with null rather than throwing, so a rejected add would otherwise pass
      // unremarked. Live logged it too and showed the shopper nothing; the tile has nowhere to put
      // a message, so this keeps the same bargain.
      console.error('Error adding to cart:', variantId);
    }
  } finally {
    // Cleared only once the drawer is on its way, so the spinner runs for the whole wait rather
    // than leaving the tile looking idle through the pause.
    delete button.dataset.busy;
    button.removeAttribute('aria-busy');
    button.blur();
  }
};

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest(ACTION);
  if (!(button instanceof HTMLElement)) return;

  event.preventDefault();
  addVariant(button);
});
